import React, { useMemo, useEffect, useState } from 'react';
import { Progress } from 'antd';
import { StyledParagraph, StyledTitle } from "../CustomAntdComponents/Typography";
import {
    Content,
    InfoTextWrapper,
    ImageWrapper,
    InfoWrapper,
    TextWrapper,
    Wrapper,
    BusItem,
    BoxesWrapper,
    BoxItem,
    BusInfo,
    StepContainer,
    ContentContainer,
    StatusBarWrapper,
    StatusBarIcon,
    StatusBarText,
    StatusBarIndicator,
    DashedBox,
    SkeletonLine,
    FloatingIconWrapper,
    CodeIconWrapper,
} from "./styles";
import { ReactComponent as RightArrowIcon } from "../../assets/images/arrow-right.svg";
import { ReactComponent as DirectionIcon } from "../../assets/images/direction-hr.svg";
import { ReactComponent as BusIcon } from '../../assets/images/bus-grey.svg';
import { pluralizeText } from "../../utils/helpers";
import { useStore } from "../../store/global/appStore";
import { getTerminalName } from "../TripSummary/helper";
import { PRIMARY } from "../../utils/colors";
import useIsMobile from "../../hooks/useIsMobile";
import { useRoadSearchStore } from "../../store/roadSearch/roadSearchStore";
import { formatRoadDateRange } from '../../utils/roadDate';

import {
  Plain,
  VerifiedCheck,
  Tram,
  MapPoint,
  Telescope,
  LightbulbMinimalistic,
  ClockCircle,
} from "@solar-icons/react";

type Props = {
    booking?: boolean;
};

/* ───── Status bar (reused across all 3 steps) ───── */
const StatusBar = ({
  text,
  icon,
}: {
  text: string;
  icon: React.ReactNode;
}) => (
  <StatusBarWrapper>
    <StatusBarIcon>{icon}</StatusBarIcon>
    <StatusBarText>{text}</StatusBarText>
    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
      <div style={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.2)", marginRight: "12px" }} />
      <StatusBarIndicator>
        <div className="inner-square" />
      </StatusBarIndicator>
    </div>
  </StatusBarWrapper>
);

const BusRequestLoading = ({ booking }: Props) => {
    const [percent, setPercent] = useState<number>(5);
    const [step, setStep] = useState(1);
    const { isMobile } = useIsMobile();
    const {
        formData: {
            numberOfAdults, TripType,
            ArrivalDate, DepartureDate, DepartureId, ArrivalId
        },
    } = useRoadSearchStore();
    const { roadRoutes } = useStore();

    const numberOfPassengers = useMemo(() => {
        return numberOfAdults;
    }, [numberOfAdults]);

    const isRoundTrip = TripType === 'round_trip';
    const departure = getTerminalName(roadRoutes, DepartureId);
    const arrival = getTerminalName(roadRoutes, ArrivalId);

    const getLabel = (label: string) => {
        return label.split('(')[0].slice(0, 10);
    }
    const departureLabel = useMemo(() => {
        return getLabel(departure);
    }, [departure]);

    const arrivalLabel = useMemo(() => {
        return getLabel(arrival);
    }, [arrival]);

    const dynamicProgress = () => {
        const newPercent = percent + 1;

        if (newPercent > 100) {
            setPercent(60);
        } else {
            setPercent(newPercent);
        }
    }

    useEffect(() => {
        const intervalId = setInterval(dynamicProgress, 1000);
        return () => clearInterval(intervalId);
    });

    useEffect(() => {
        const intervalId = setInterval(() => {
            setStep((prevStep) => {
                if (prevStep === 1) return 2;
                if (prevStep === 2) return 3;
                return 1;
            });
        }, 3000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <>
            {
                !isMobile && <Progress strokeColor={PRIMARY} percent={percent} type="line" showInfo={false} size="small" />
            }
            <Wrapper>
                <Content>
                    <ContentContainer>
                        {/* Step 1: Bus Info Card */}
                        <StepContainer active={step === 1}>
                            <InfoWrapper>
                                <BusInfo>
                                    <BusItem>
                                        <StyledParagraph size="small" align="left">
                                            {departure}
                                        </StyledParagraph>
                                        <StyledTitle>
                                            {departureLabel}
                                        </StyledTitle>
                                    </BusItem>

                                    {
                                        isRoundTrip ? <DirectionIcon /> : <RightArrowIcon />
                                    }

                                    <BusItem>
                                        <StyledParagraph size="small" align="left">
                                            {arrival}
                                        </StyledParagraph>
                                        <StyledTitle>
                                            {arrivalLabel}
                                        </StyledTitle>
                                    </BusItem>
                                </BusInfo>
                                <TextWrapper>
                                    <StyledParagraph align="center">
                                        {formatRoadDateRange(DepartureDate, ArrivalDate, "ddd, MMM D YYYY")}
                                    </StyledParagraph>
                                </TextWrapper>

                                <TextWrapper>
                                    <StyledParagraph align="center">
                                        {`${pluralizeText(numberOfPassengers, "Passenger", "Passengers")}`}
                                    </StyledParagraph>
                                </TextWrapper>

                                <BoxesWrapper>
                                    {new Array(30).fill(0).map((_, index) => (
                                        <BoxItem key={index} index={index}>&nbsp;</BoxItem>
                                    ))}
                                </BoxesWrapper>
                            </InfoWrapper>

                            <StatusBar
                                text={
                                    booking ? "Confirming your booking..." : "We are searching for the best prices..."
                                }
                                icon={
                                    <BusIcon
                                        width={16}
                                        height={16}
                                        style={{ fill: "white" }}
                                    />
                                }
                            />
                        </StepContainer>

                        {/* Step 2: Skeleton / code wireframe */}
                        <StepContainer active={step === 2}>
                            <DashedBox>
                                <SkeletonLine width="60%" />
                                <SkeletonLine width="40%" />
                                <SkeletonLine width="50%" />
                                <SkeletonLine width="80%" />
                                <SkeletonLine width="20%" />
                                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                                    <CodeIconWrapper>
                                        {"</>"}
                                    </CodeIconWrapper>
                                    <SkeletonLine width="40%" height="16px" />
                                </div>
                            </DashedBox>

                            <StatusBar
                                text="Curating the best deals."
                                icon={<VerifiedCheck weight="Outline" style={{ fontSize: 16 }} />}
                            />
                        </StepContainer>

                        {/* Step 3: Floating travel icons */}
                        <StepContainer active={step === 3}>
                            <DashedBox>
                                <FloatingIconWrapper
                                    top="35px"
                                    left="65px"
                                    delay="0s"
                                    fontSize="28px"
                                >
                                    <Plain weight="Outline" />
                                </FloatingIconWrapper>

                                <FloatingIconWrapper
                                    top="40px"
                                    left="185px"
                                    delay="0.5s"
                                    fontSize="26px"
                                >
                                    <VerifiedCheck weight="Outline" />
                                </FloatingIconWrapper>

                                <FloatingIconWrapper
                                    top="110px"
                                    left="45px"
                                    delay="1s"
                                    fontSize="28px"
                                >
                                    <Telescope weight="Outline" />
                                </FloatingIconWrapper>

                                <FloatingIconWrapper
                                    top="95px"
                                    left="145px"
                                    delay="1.5s"
                                    fontSize="32px"
                                >
                                    <Tram weight="Outline" />
                                </FloatingIconWrapper>

                                <FloatingIconWrapper
                                    top="155px"
                                    left="100px"
                                    delay="2s"
                                    fontSize="28px"
                                >
                                    <MapPoint weight="Outline" />
                                </FloatingIconWrapper>

                                <FloatingIconWrapper
                                    top="145px"
                                    left="195px"
                                    delay="2.5s"
                                    fontSize="28px"
                                >
                                    <LightbulbMinimalistic weight="Outline" />
                                </FloatingIconWrapper>
                            </DashedBox>

                            <StatusBar
                                text="Almost done."
                                icon={<ClockCircle weight="Outline" style={{ fontSize: 16 }} />}
                            />
                        </StepContainer>
                    </ContentContainer>
                </Content>
            </Wrapper>
        </>
    );
};

export default BusRequestLoading;
